import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import React, { ForwardedRef, MouseEvent, forwardRef, useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';

import ActionDialog from '~components/ActionDialog';
import OberonCard from '~components/OberonCard';

type Prop = {
  title: string;
  subTitle?: string;
  id?: string;
  deleteMessage?: string;
  draggableProvided?: DraggableProvided;
  onEdit?: () => void;
  onDelete?: () => Promise<void>;
};

const GenericDataCard = forwardRef(
  (
    { title, subTitle, id, deleteMessage, draggableProvided, onEdit, onDelete }: Prop,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState<boolean>(false);

    const handleMenuOpen = (e: MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(e.currentTarget);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
    };

    const triggerEdit = () => {
      if (!onEdit) {
        console.error('triggerEdit: onEdit not defined');
        return;
      }

      setAnchorEl(null);
      onEdit();
    };

    const triggerDelete = async () => {
      if (!onDelete) {
        console.error('triggerDelete: onDelete not defined');
        return;
      }

      try {
        await onDelete();
        setDeleteConfirmModalOpen(false);
      } catch (e) {
        return;
      }
    };

    const handleDeleteConfirmationOpen = () => {
      setAnchorEl(null);
      setDeleteConfirmModalOpen(true);
    };

    const handleDeleteConfirmationClose = () => {
      setDeleteConfirmModalOpen(false);
    };

    return (
      <>
        <OberonCard
          ref={ref}
          titleFontWeight={700}
          title={title}
          subHeader={subTitle}
          draggableProvided={draggableProvided}
          action={
            <>
              <IconButton aria-controls={id} aria-haspopup={id ? true : undefined} onClick={handleMenuOpen}>
                <MoreHorizIcon />
              </IconButton>

              {(onEdit || onDelete) && (
                <Menu id={id} anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleMenuClose}>
                  {onEdit && <MenuItem onClick={triggerEdit}>Edit</MenuItem>}
                  {onDelete && <MenuItem onClick={handleDeleteConfirmationOpen}>Delete</MenuItem>}
                </Menu>
              )}
            </>
          }
        />

        <ActionDialog
          open={deleteConfirmModalOpen}
          title='Are you sure you want to do this?'
          content={deleteMessage}
          onClose={handleDeleteConfirmationClose}
          onAccept={triggerDelete}
          primaryActionTitle='Delete'
        />
      </>
    );
  },
);

export default GenericDataCard;
